import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Register fuer fixierte Leisten an der unteren Fensterkante (Workbench-Review,
 * Workbench-Sammelaktionen, Feed-Check-CTA).
 *
 * Hintergrund: der schwebende Feedback-Knopf sitzt unten rechts auf
 * `z-index: 99`, die Leisten auf `z-index: 50`. Bei 375px ueberlappte der Knopf
 * die Hauptaktion "Freigeben" um rund 532px^2 und schluckte, weil er oben
 * liegt, deren Klicks. Die Stapelreihenfolge zu tauschen haette nicht geholfen,
 * dann waere der Knopf der teilweise Verdeckte. Also weicht der Knopf aus: jede
 * Leiste meldet ihre Hoehe an, solange sie montiert ist, und der Knopf hebt
 * sich ueber die hoechste angemeldete Leiste.
 *
 * Der veroeffentlichte Wert ist das MAXIMUM ueber alle Anmeldungen. Das setzt
 * voraus, dass hoechstens EINE dieser Leisten gleichzeitig die Kante besetzt --
 * fuer die Aktionsleisten der App gilt das.
 *
 * Genau diese Voraussetzung ist der Grund, warum die Registratur eine Fabrik
 * ist und kein Modulzustand: ein Bewohner der Kante, der NEBEN einer Leiste
 * stehen kann statt an ihrer Stelle, gehoert nicht in dieses Register -- das
 * Maximum aus beiden ist nicht ihre Summe, und der Maximum-Wert enthaelt
 * ausserdem den Leser selbst. So ein Fall waere ein zweiter Aufruf von
 * `createBarRegistry`, kein Umbau dieser Datei. Heute gibt es genau einen.
 */

/** Height assumed for a bar that reports 0px. Happens before first layout and
 *  in non-layouting environments (jsdom). Covers the tallest bar we ship. */
export const STICKY_ACTION_BAR_FALLBACK_HEIGHT = 96;

/** Breathing room between the top edge of a bar and the floating button. */
export const STICKY_ACTION_BAR_CLEARANCE = 16;

/**
 * Wie weit ein angemeldetes Element die Kante belegt, in px. `0` bedeutet
 * "nicht messbar" und loest den Fallback aus -- NICHT "belegt nichts".
 */
type Messung = (element: HTMLElement) => number;

function createBarRegistry(messen: Messung, fallback: number) {
  const heights = new Map<symbol, number>();
  const listeners = new Set<() => void>();
  let snapshot = 0;

  function publish() {
    let tallest = 0;
    for (const height of heights.values()) {
      if (height > tallest) tallest = height;
    }
    if (tallest === snapshot) return;
    snapshot = tallest;
    for (const listener of listeners) listener();
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getSnapshot() {
    return snapshot;
  }

  function useHeight(): number {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }

  function useRegister<T extends HTMLElement>(enabled = true) {
    const ref = useRef<T | null>(null);
    const keyRef = useRef<symbol | null>(null);
    if (keyRef.current === null) keyRef.current = Symbol('bottom-bar');

    const measure = useCallback(() => {
      const measured = ref.current ? messen(ref.current) : 0;
      heights.set(keyRef.current as symbol, measured > 0 ? measured : fallback);
      publish();
    }, []);

    useLayoutEffect(() => {
      const key = keyRef.current as symbol;
      if (!enabled) {
        heights.delete(key);
        publish();
        return;
      }
      measure();
      window.addEventListener('resize', measure);
      // K1-Befund: die Leiste aendert ihre Hoehe auch OHNE Fenster-Resize —
      // Reiterwechsel tauscht die Knopfzeile (zwei Handlungen vs. eine plus
      // Rueckfrage), Textumbruch bei langen Zaehlern. Nur mount + resize zu
      // messen liess den Abstand des Feedback-Buttons auf dem alten Stand.
      let observer: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined' && ref.current) {
        observer = new ResizeObserver(measure);
        observer.observe(ref.current);
      }
      return () => {
        observer?.disconnect();
        window.removeEventListener('resize', measure);
        heights.delete(key);
        publish();
      };
    }, [enabled, measure]);

    return ref;
  }

  return { useHeight, useRegister };
}

const actionBars = createBarRegistry(
  // Aktionsleisten liegen buendig an der Kante, ihre Hoehe IST ihr Versatz.
  (element) => element.getBoundingClientRect().height,
  STICKY_ACTION_BAR_FALLBACK_HEIGHT,
);

/** Height in px of the tallest mounted sticky action bar, `0` when there is none. */
export const useStickyActionBarHeight = actionBars.useHeight;

/**
 * Registers a fixed bottom action bar for as long as the component is mounted.
 * Attach the returned ref to the bar's root element.
 *
 * `enabled` exists for bars that render conditionally but must still call the
 * hook unconditionally (a bar that returns null while its primary CTA is on
 * screen). Pass `false` and the bar stays out of the registry.
 */
export const useStickyActionBar = actionBars.useRegister;
